import type { CreatureSpeedSource } from '../types';

/** True when any provenance row carries a value_factor multiplier. */
export function creatureSpeedFactorRowsPresent(sources: readonly CreatureSpeedSource[]): boolean {
    return sources.some(source => typeof source.value_factor === 'number');
}

/**
 * Port of ft_speed_apply_factor_rows(): multiplier rows (Slowed's "0.5*[speed_walking]") apply once
 * after the additive total. Lowest slow (<1) and highest boost (>1) each win; a bracket lock beats both.
 * The winning row's value becomes the signed delta; factor-row values already in the naive total
 * (a replayed ledger) are subtracted first. Mutates `sources` in place like the PHP by-ref array.
 */
export function applyCreatureSpeedFactorRows(
    sources: CreatureSpeedSource[],
    naiveTotal: number,
    locked: boolean,
): { total: number; prefactor: number } {
    let slow: number | null = null;
    let boost: number | null = null;
    let carried = 0;
    for (let index = 0; index < sources.length; index++) {
        const source = sources[index];
        if (!source || typeof source.value_factor !== 'number') continue;
        carried += Math.trunc(source.value);
        const factor = source.value_factor;
        if (factor < 1 && (slow === null || factor < (sources[slow]?.value_factor as number))) slow = index;
        else if (factor > 1 && (boost === null || factor > (sources[boost]?.value_factor as number))) boost = index;
        sources[index] = { ...source, value: 0 };
    }
    if (locked) return { total: naiveTotal, prefactor: naiveTotal };
    const prefactor = Math.max(0, naiveTotal - carried);
    let total = prefactor;
    for (const index of [slow, boost]) {
        if (index === null) continue;
        const source = sources[index];
        if (!source) continue;
        const next = phpRound(total * (source.value_factor as number));
        sources[index] = { ...source, value: next - total };
        total = next;
    }
    return { total: Math.max(0, total), prefactor };
}

/** PHP round(): half away from zero. */
function phpRound(value: number): number {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}

// ---- speed_cannot_be_reduced (Wizard v9, Transmutation L40 "Ever-Changing Form", 2026-10-09) ---------------------------------------------
// "For 1 minute after combat begins … your movement cannot be reduced." The flag counts ONLY on a HELD status row — the combat-start TIMED
// passive's window status (its bucket copy never counts: compute_speeds' speedPassiveGate / cast-range-gate's statusDeliveredPassive rule).
// While held, every reducing speed row is ignored: a negative additive row, a slow factor (< 1) and a `{N}` lock below the unlocked total.
// The rule lives in the browser engine only (no PHP compile reads the layer).
export const SPEED_CANNOT_BE_REDUCED_LAYER = 'speed_cannot_be_reduced';
type SpeedShieldValue = (row: never, name: string) => unknown;
const truthyFlag = (value: unknown): boolean => {
    let v = value;
    while (Array.isArray(v)) v = v[0];
    if (v !== null && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, '0')) v = (v as Record<string, unknown>)['0'];
    return v !== null && v !== undefined && v !== false && v !== '' && v !== '0' && v !== 0;
};
/** A held status DEFINITION shields speed: its own layer, or a passive its `passive_ability` layer attaches (the item-as-status pattern). */
export function statusDefinitionShieldsSpeed(def: unknown, layerValue: SpeedShieldValue,
    attached?: (def: unknown) => readonly unknown[]): boolean {
    if (def === null || typeof def !== 'object') return false;
    if (truthyFlag(layerValue(def as never, SPEED_CANNOT_BE_REDUCED_LAYER))) return true;
    return (attached?.(def) ?? []).some(p => p !== null && typeof p === 'object' && truthyFlag(layerValue(p as never, SPEED_CANNOT_BE_REDUCED_LAYER)));
}
/** A speed provenance row that REDUCES: a slow factor (< 1), or a negative additive row (a brace row is judged against the total). */
export function creatureSpeedSourceReduces(source: CreatureSpeedSource): boolean {
    if (typeof source.value_factor === 'number') return source.value_factor < 1;
    if (typeof (source as { value_brace?: unknown }).value_brace === 'number') return false;
    return source.value < 0;
}
/**
 * Fold a speed twice when shielded and a `{N}` lock is present: locked and unlocked (lock rows dropped); the higher total wins, so a lock
 * that would reduce is ignored while a lock that RAISES ("{60}") still applies. Unshielded / unlocked: the one ordinary fold.
 */
export function foldShieldedSpeed<T extends { total: number }>(shield: boolean, sources: readonly CreatureSpeedSource[], brace: number | null,
    fold: (sources: CreatureSpeedSource[], brace: number | null) => T): T {
    const kept = shield ? sources.filter(source => !creatureSpeedSourceReduces(source)) : [...sources];
    if (!shield || brace === null) return fold(kept, brace);
    const locked = fold(kept, brace);
    const open = fold(kept.filter(source => typeof (source as { value_brace?: unknown }).value_brace !== 'number'), null);
    return open.total > locked.total ? open : locked;
}
